import { Component, Prop, Element, State, Event, Host, EventEmitter, h, Watch } from '@stencil/core';
import { queryShadowRoot, isHidden, isFocusable } from '@utils/focus-trap';
import { animationTo, KEYFRAMES } from '@utils/animation';

type CloseEventTrigger = 'CLOSE_BUTTON' | 'ESCAPE_KEY' | 'BACKDROP';

export interface BeforeCloseEventDetail {
  trigger: CloseEventTrigger;
}

@Component({
  tag: 'spw-modal',
  styleUrl: 'spw-modal.scss',
  shadow: true,
})
export class SpwModal {
  /** Indique si la modal est ouverte ou fermée, mutable et reflétée dans le DOM */
  @Prop({ reflect: true, mutable: true }) opened?: boolean = false;

  @State() showModal: boolean = this.opened || false;

  /** Titre de la modal */
  @Prop() caption: string = 'Titre de la modal';
  /** Indique si la fermeture de la modal est possible en cliquant sur l'overlay */
  @Prop() closeOnOverlayClick: boolean = true;
  /** Rend les boutons en bas de la modal "collants" (toujours visibles) si vrai */
  @Prop() stickyButtons: boolean = false;
  /** Événement déclenché lors de l'ouverture de la modal */
  @Event() spwModalOpen: EventEmitter;
  /** Événement déclenché lors de la fermeture de la modal */
  @Event() spwModalClose: EventEmitter;
  /** Définit la taille de la modal : 'small', 'medium', 'large' ou 'fluid' */
  @Prop() size: 'small' | 'medium' | 'large' | 'fluid' = 'small';
  /** Icône d'alerte à afficher dans la modal */
  @Prop() alertIcon: string = '';
  /** Texte du bouton de confirmation */
  @Prop() okButtonLabel: string = 'Confirmer';
  /** Texte du bouton d'annulation */
  @Prop() cancelButtonLabel: string = 'Annuler';

  @Element() hostElement: HTMLSpwModalElement;

  @State() slotButtonsPresent: boolean = false;

  /** Indique si le bouton de fermeture est visible */
  @Prop() showCloseButton: boolean = true;

  private modalContainer: HTMLElement;
  private focusableElements: HTMLElement[] = [];
  private closeButton: HTMLSpwIconElement;

  componentDidLoad() {
    this.focusableElements = queryShadowRoot(this.hostElement.shadowRoot, el => isHidden(el) || el.matches('[data-focus-trap-edge]'), isFocusable);
    document.addEventListener('click', this.handleClick);
    document.addEventListener('click', this.handleClose);
    document.addEventListener('keydown', this.handleKeypress);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClick);
    document.removeEventListener('click', this.handleClose);
    document.removeEventListener('keydown', this.handleKeypress);
  }

  private handleClick = (event: MouseEvent) => {
    const trigger = (event.target as HTMLElement).closest('[data-modal-trigger]');
    if (trigger) {
      const modalId = trigger.getAttribute('data-modal-trigger');
      if (modalId === this.hostElement.id) {
        this.opened = true;
      }
    }
  };

  private handleClose = (event: MouseEvent) => {
    const trigger = (event.target as HTMLElement).closest('[data-modal-close]');
    if (trigger) {
      const modalId = trigger.getAttribute('data-modal-close');
      if (modalId === this.hostElement.id) {
        this.opened = false;
      }
    }
  };

  private getFirstFocusableElement(): HTMLElement | null {
    return this.focusableElements[0];
  }

  private getLastFocusableElement(): HTMLElement | null {
    return this.focusableElements[this.focusableElements.length - 1];
  }

  private handleTopFocus = () => {
    this.attemptFocus(this.getLastFocusableElement());
  };

  private handleBottomFocus = () => {
    this.attemptFocus(this.getFirstFocusableElement());
  };

  private attemptFocus(element: HTMLElement | null) {
    if (element == null) {
      setTimeout(() => {
        this.closeButton.focus();
      });
      return;
    }

    setTimeout(() => {
      element.focus();
    }, 0);
  }

  private open() {
    this.showModal = true;
    try {
      const anim = animationTo(this.modalContainer, KEYFRAMES.fadeIn, {
        duration: 200,
      });
      anim.addEventListener('finish', () => {
        setTimeout(() => {
          this.getLastFocusableElement()?.focus();
          this.getLastFocusableElement()?.blur();
        }, 0);

        this.spwModalOpen.emit();
      });
    } catch (err) {
      this.spwModalOpen.emit();
    }
  }

  private close() {
    try {
      const anim = animationTo(this.modalContainer, KEYFRAMES.fadeOut, {
        duration: 200,
      });
      anim.addEventListener('finish', () => {
        this.showModal = false;
        this.spwModalClose.emit();
      });
    } catch (err) {
      this.showModal = false;
      this.spwModalClose.emit();
    }
  }

  private handleKeypress = (event: KeyboardEvent) => {
    if (this.showModal && event.key === 'Escape') {
      this.doBeforeClose('ESCAPE_KEY');
    }
  };

  private doBeforeClose(trigger: CloseEventTrigger) {
    const triggers = [];
    triggers.push(trigger);
    const prevented = triggers.some(event => event.defaultPrevented);
    if (!prevented) {
      this.opened = false;
    }
  }

  @Watch('opened')
  openedChanged(newValue: boolean) {
    newValue === true ? this.open() : this.close();
  }

  private handleOverlayClick() {
    if (this.closeOnOverlayClick) {
      this.doBeforeClose('BACKDROP');
    }
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-modal': true,
      'spw-modal--opened': this.showModal,
      'spw-modal--sticky-buttons': this.stickyButtons,
    };
  }

  render() {
    return (
      <Host>
        <div ref={el => (this.modalContainer = el)} class={this.elementClass}>
          <div class="spw-modal__overlay" onClick={() => this.handleOverlayClick()}></div>
          <div data-focus-trap-edge onFocus={this.handleTopFocus} tabindex="0"></div>
          <div class={`spw-modal__content-container spw-modal__content-container--${this.size}`} role="dialog" aria-modal="true" aria-label={this.caption}>
            <div class="spw-modal__content">
              <div class="spw-modal__header">
                <h2 class="spw-modal__caption">{this.caption}</h2>
                {this.showCloseButton && (
                  <spw-icon class="spw-modal__close-button" ref={el => (this.closeButton = el)} icon="fa-xmark" onClick={() => this.doBeforeClose('CLOSE_BUTTON')}></spw-icon>
                )}
              </div>
              <div class="spw-modal__body">
                <slot name="content" />
              </div>
              <div class="spw-modal__buttons">
                <slot name="buttons"></slot>
              </div>
            </div>
          </div>
          <div data-focus-trap-edge onFocus={this.handleBottomFocus} tabindex="0"></div>
        </div>
      </Host>
    );
  }
}
